import { useTranslation } from "react-i18next";

import { Typography, useToast } from "@aviary";
import FullscriptLogoSvg from "@shared/assets/logo/fullscript-logo-green-leaf-pigeon-text.svg";
import WellevateByFullscriptLogo from "@shared/assets/logo/wellevate-by-fullscript.png";
import { GoogleSSOButton } from "@shared/components";
import { useWellevateAccountActivation } from "@shared/data/mutations";
import { useBreakpoints } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";
import { FormWrapper, SignInForm, SignInWrapper } from "@unauthenticated/shared/components";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { useTreatmentPlanTerm } from "@unauthenticated/shared/hooks/useTreatmentPlanTerm";
/* eslint-disable @fullscript/cross-reference */
import {
  SignUpModalHeader,
  SignUpModalTitle,
  StoreLogo,
  StoreOffersPill,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/wellevateAuthorize/patients/locales/i18n";

import * as styles from "../WellevateSignInSignUp.styles";
import { WellevateStepWrapper } from "../WellevateStepWrapper";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
}

const WellevateStepSignIn = ({ patientAttributes, additionalAttributes, ssoErrors }: Props) => {
  const treatmentPlanTermPlural = useTreatmentPlanTerm({ plural: true });
  const { tablet } = useBreakpoints();
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const { search } = useLocation();

  const query = new URLSearchParams(search);
  const token = query.get("token");

  const [activateWellevateAccount] = useWellevateAccountActivation({
    onCompleted: () => window.location.assign("/"),
    onError: ({ message }) => makeToast("error", message),
  });

  const { storeLogo, storeName, treatmentPlanSlug, uuid, labRecommendationId, storeSlug } =
    patientAttributes;

  const handleSignInCompleted = () => {
    activateWellevateAccount({
      variables: {
        input: {
          token,
          activate: true,
        },
      },
    });
  };

  const bodyText = t(l.common.header.content);

  const bodySubText = t(l.common.header.subContent, { treatmentPlanTermPlural });

  return (
    <WellevateStepWrapper
      headerText={t(l.common.header.title)}
      bodyText={bodyText}
      bodySubText={bodySubText}
    >
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        {tablet.lessThan && (
          <section css={styles.section}>
            <div css={styles.logos}>
              <FullscriptLogoSvg css={styles.logo} />
              <img
                src={WellevateByFullscriptLogo}
                css={styles.logo}
                alt={t(l.companies.Wellevate)}
              />
            </div>
            <Typography type="h2" sizeOverride="h4">
              {t(l.common.header.title)}
            </Typography>
            <Typography>{bodyText}</Typography>
          </section>
        )}
      </SignUpModalHeader>
      <FormWrapper>
        <SignInWrapper css={styles.form}>
          <SSOErrorBox ssoErrors={ssoErrors} />
          <GoogleSSOButton
            isOrTextRendered
            signUpPage="wellevate_patients_authorize_sign_in_page"
            pageVersion="react_with_sso"
            treatmentPlanSlug={treatmentPlanSlug}
            labRecommendationId={labRecommendationId}
            storeSlug={storeSlug}
            uuid={uuid}
          />
          <SignInForm
            additionalAttributes={additionalAttributes}
            forgotPasswordLink={authRoutes.welcome_forgot_password(
              storeSlug,
              uuid || treatmentPlanSlug
            )}
            onCompleted={handleSignInCompleted}
            defaultEmail={patientAttributes.email}
          />
        </SignInWrapper>
      </FormWrapper>
    </WellevateStepWrapper>
  );
};

export { WellevateStepSignIn };
